import Cookies from 'js-cookie';

// All known cookie names that need to be cleared on logout (both auth types)
const AUTH_COOKIE_NAMES = [
    // Auth tokens
    '11-267d33f6-2969-42f9-b371-848cbcf8aad1',
    'refresh_token',
    'user_data',
    'auth_token',
    'access_token',
    // Session cookies
    'sessionid',
    'csrftoken',
    // Team/User related
    'team_token',
    'team_user',
    // Legacy cookies
    'waterat',
];

// localStorage keys for MERCHANT AUTH (regular login)
const MERCHANT_AUTH_STORAGE_KEYS = [
    'persist:root',
    'persist:auth',
    'auth_token',
    'access_token',
    'refresh_token',
    'user_data',
    'auth_user',
];

// localStorage keys for TEAM USER AUTH
const TEAM_USER_STORAGE_KEYS = [
    'team_auth',
    'isTeamUser',
    'teamUserData',
    'team_user',
    'team_token',
];

// Combined keys for complete logout
const ALL_AUTH_STORAGE_KEYS = [
    ...MERCHANT_AUTH_STORAGE_KEYS,
    ...TEAM_USER_STORAGE_KEYS,
];

class ClearAuthCookiesService {
    /**
     * Check if a cookie name is a Team User cookie (starts with TM--)
     */
    public isTeamUserCookie(cookieName: string): boolean {
        return cookieName.startsWith('TM--');
    }

    /**
     * Check if a cookie name is a Merchant Auth cookie (UUID format without TM-- prefix)
     */
    public isMerchantAuthCookie(cookieName: string): boolean {
        // UUID pattern without TM-- prefix
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
        return uuidPattern.test(cookieName) && !cookieName.startsWith('TM--');
    }

    /**
     * Get the current user type based on localStorage
     */
    public getCurrentUserType(): 'team' | 'merchant' | null {
        const isTeamUser = localStorage.getItem('isTeamUser') === 'true';
        const teamAuth = localStorage.getItem('team_auth');
        
        if (isTeamUser || teamAuth) {
            return 'team';
        }
        
        const authUser = localStorage.getItem('auth_user') || localStorage.getItem('user_data');
        if (authUser) {
            return 'merchant';
        }
        
        return null;
    }

    /**
     * Clear ONLY Team User cookies (cookies starting with TM--)
     */
    public clearTeamUserCookies(): void {
        const allCookies = document.cookie.split(';');
        const domain = window.location.hostname;
        
        allCookies.forEach(cookie => {
            const [name] = cookie.trim().split('=');
            if (name && this.isTeamUserCookie(name)) {
                Cookies.remove(name);
                Cookies.remove(name, { path: '/' });
                Cookies.remove(name, { path: '/', domain });
                if (domain !== 'localhost') {
                    Cookies.remove(name, { path: '/', domain: `.${domain}` });
                }
            }
        });
    }

    /**
     * Clear ONLY Merchant Auth cookies (UUID format without TM-- prefix)
     */
    public clearMerchantAuthCookies(): void {
        const allCookies = document.cookie.split(';');
        const domain = window.location.hostname;
        
        allCookies.forEach(cookie => {
            const [name] = cookie.trim().split('=');
            if (name && this.isMerchantAuthCookie(name)) {
                Cookies.remove(name);
                Cookies.remove(name, { path: '/' });
                Cookies.remove(name, { path: '/', domain });
                if (domain !== 'localhost') {
                    Cookies.remove(name, { path: '/', domain: `.${domain}` });
                }
            }
        });
    }

    /**
     * Clear ALL auth cookies using js-cookie library
     * This properly removes cookies with correct path/domain settings
     */
    public clearAuthCookies(): void {
        const domain = window.location.hostname;

        // Clear all known auth cookies using js-cookie
        AUTH_COOKIE_NAMES.forEach(cookieName => {
            Cookies.remove(cookieName);
            Cookies.remove(cookieName, { path: '/' });
            Cookies.remove(cookieName, { path: '/', domain });
            if (domain !== 'localhost') {
                Cookies.remove(cookieName, { path: '/', domain: `.${domain}` });
            }
        });

        // Clear all dynamic cookies (both TM-- and UUID format)
        const allCookies = document.cookie.split(';');
        allCookies.forEach(cookie => {
            const [name] = cookie.trim().split('=');
            if (name) {
                Cookies.remove(name);
                Cookies.remove(name, { path: '/' });
                Cookies.remove(name, { path: '/', domain });
                if (domain !== 'localhost') {
                    Cookies.remove(name, { path: '/', domain: `.${domain}` });
                }
            }
        });
    }

    /**
     * Clear ONLY Team User localStorage items
     */
    public clearTeamUserLocalStorage(): void {
        TEAM_USER_STORAGE_KEYS.forEach(key => {
            localStorage.removeItem(key);
        });
    }

    /**
     * Clear ONLY Merchant Auth localStorage items
     */
    public clearMerchantAuthLocalStorage(): void {
        MERCHANT_AUTH_STORAGE_KEYS.forEach(key => {
            localStorage.removeItem(key);
        });
    }

    /**
     * Clear all auth-related localStorage items (both types)
     */
    public clearLocalStorage(): void {
        ALL_AUTH_STORAGE_KEYS.forEach(key => {
            localStorage.removeItem(key);
        });
    }

    /**
     * Clear all sessionStorage
     */
    public clearSessionStorage(): void {
        sessionStorage.clear();
    }

    /**
     * Team User Logout: Clear only team user data and redirect
     * @param skipRedirect - If true, don't redirect
     */
    public teamUserLogout(skipRedirect: boolean = false): void {
        // Clear only team user data
        this.clearTeamUserLocalStorage();
        this.clearTeamUserCookies();
        
        // Set logout flag
        sessionStorage.setItem('user_logged_out', 'true');

        // Redirect to login page (unless skipped)
        if (!skipRedirect) {
            setTimeout(() => {
                window.location.replace('/auth/login');
            }, 100);
        }
    }

    /**
     * Merchant Auth Logout: Clear only merchant data and redirect
     * @param skipRedirect - If true, don't redirect
     */
    public merchantAuthLogout(skipRedirect: boolean = false): void {
        // Clear only merchant auth data
        this.clearMerchantAuthLocalStorage();
        this.clearMerchantAuthCookies();
        
        // Set logout flag
        sessionStorage.setItem('user_logged_out', 'true');

        // Redirect to login page (unless skipped)
        if (!skipRedirect) {
            setTimeout(() => {
                window.location.replace('/auth/login');
            }, 100);
        }
    }

    /**
     * Complete logout: Clear all auth data and redirect to login
     * @param dispatch - Optional Redux dispatch function
     * @param skipRedirect - If true, don't redirect (useful when caller handles redirect)
     */
    public completeLogout(dispatch?: any, skipRedirect: boolean = false): void {
        // Dispatch Redux logout first if available
        if (dispatch) {
            dispatch({ type: 'auth/logout' });
        }

        // Clear all auth data
        this.clearLocalStorage();
        this.clearSessionStorage();
        this.clearAuthCookies();

        // Redirect to login page (unless skipped)
        if (!skipRedirect) {
            setTimeout(() => {
                window.location.replace('/auth/login');
            }, 100);
        }
    }

    /**
     * Smart logout based on current user type
     * Automatically detects if user is team user or merchant and clears appropriate data
     */
    public smartLogout(skipRedirect: boolean = false): void {
        const userType = this.getCurrentUserType();
        
        if (userType === 'team') {
            this.teamUserLogout(skipRedirect);
        } else if (userType === 'merchant') {
            this.merchantAuthLogout(skipRedirect);
        } else {
            // Unknown user type, clear everything
            this.completeLogout(undefined, skipRedirect);
        }
    }

    /**
     * Quick logout without redirect - useful when you want to handle redirect yourself
     */
    public clearAllAuthData(): void {
        this.clearLocalStorage();
        this.clearSessionStorage();
        this.clearAuthCookies();
    }

    /**
     * Clear only team user auth data without redirect
     */
    public clearTeamUserAuthData(): void {
        this.clearTeamUserLocalStorage();
        this.clearTeamUserCookies();
    }

    /**
     * Clear only merchant auth data without redirect
     */
    public clearMerchantAuthData(): void {
        this.clearMerchantAuthLocalStorage();
        this.clearMerchantAuthCookies();
    }
}

// Create singleton instance
export const clearAuthCookiesService = new ClearAuthCookiesService();
export { ClearAuthCookiesService };
